import { create } from 'zustand';
import { getPlanningEntries } from '../api/generated/planning-entries/planning-entries';
import type {
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesBulkUpdateTasksRequest,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesBulkUpdateTasksResponse,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesCreatePlanningEntryRequest,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryDto,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesAddTaskToPlanningEntryRequest,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesAssignTaskToEmployeesRequest,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesUpdatePlanningEntryRequest,
} from '../api/generated/models';
import { isoDate } from '../utils/dateUtils';
import type { DriverRole } from './useTimeclockStore';

const api = getPlanningEntries();

interface PlanningState {
  entries: FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto[];
  loading: boolean;
  error?: string;

  // Actions
  fetchWeekEntries: (startDate: string, endDate: string, employeeId?: string) => Promise<void>;
  refreshEntry: (planningEntryId: string) => Promise<void>; // Partial refresh
  refreshMultipleEntries: (planningEntryIds: string[]) => Promise<void>; // Refresh multiple entries
  createEntry: (request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesCreatePlanningEntryRequest) => Promise<FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryDto>;
  updateEntry: (id: string, request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesUpdatePlanningEntryRequest) => Promise<void>;
  /** Plans vehicle and role of a work day; both null clears the plan (FACF-118). */
  setPlannedVehicle: (id: string, vehicleId: string | null, driverRole: DriverRole | null) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  addTaskToEntry: (planningEntryId: string, taskId: string, startTime: string, endTime: string) => Promise<void>;
  /**
   * Plans one task on the existing WorkDay entry of every given employee on `date` —
   * all-or-nothing on the server (FACF-111).
   */
  assignTaskToEmployees: (
    request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesAssignTaskToEmployeesRequest
  ) => Promise<void>;
  removeTaskFromEntry: (planningEntryId: string, planningEntryTaskId: string) => Promise<void>;
  moveTaskToEntry: (planningEntryTaskId: string, sourcePlanningEntryId: string, targetPlanningEntryId: string, newStartTime?: string, newEndTime?: string) => Promise<void>;
  updateTask: (planningEntryId: string, planningEntryTaskId: string, startTime: string, endTime: string, notes?: string) => Promise<void>;
  bulkUpdateTasks: (
    request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesBulkUpdateTasksRequest
  ) => Promise<FacilityFlowCoreModulesPlanningDtosPlanningEntriesBulkUpdateTasksResponse>;
  generateMonthEntries: (employeeIds: string[], month: number, year: number) => Promise<void>;
}

export const usePlanningStore = create<PlanningState>((set, get) => ({
  entries: [],
  loading: false,

  fetchWeekEntries: async (startDate: string, endDate: string, employeeId?: string) => {
    set({ loading: true, error: undefined });
    try {
      const response = await api.getApiV1PlanningEntries({
        startDate,
        endDate,
        employeeId,
      });
      set({ entries: response, loading: false });
    } catch (err) {
      set({
        error: 'Fehler beim Laden der Planungseinträge',
        loading: false,
      });
      throw err;
    }
  },

  refreshEntry: async (planningEntryId: string) => {
    try {
      const updated = await api.getApiV1PlanningEntriesId(planningEntryId);
      set((state) => ({
        entries: state.entries.map((entry) =>
          entry.planningEntryId === planningEntryId ? updated : entry
        ),
      }));
    } catch (err) {
      console.error('Failed to refresh entry:', err);
      throw err;
    }
  },

  refreshMultipleEntries: async (planningEntryIds: string[]) => {
    try {
      const updates = await Promise.all(
        planningEntryIds.map(id => api.getApiV1PlanningEntriesId(id))
      );
      
      set((state) => {
        const updatedEntries = [...state.entries];
        updates.forEach(updated => {
          const index = updatedEntries.findIndex(e => e.planningEntryId === updated.planningEntryId);
          if (index !== -1) {
            updatedEntries[index] = updated;
          }
        });
        return { entries: updatedEntries };
      });
    } catch (err) {
      console.error('Failed to refresh entries:', err);
      throw err;
    }
  },

  createEntry: async (request) => {
    try {
      const response = await api.postApiV1PlanningEntries(request);
      // Add to local state immediately
      set((state) => ({ entries: [...state.entries, response] }));
      return response;
    } catch (err) {
      set({ error: 'Fehler beim Erstellen des Eintrags' });
      throw err;
    }
  },

  updateEntry: async (id: string, request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesUpdatePlanningEntryRequest) => {
    try {
      await api.putApiV1PlanningEntriesId(id, request);
      // Refresh the updated entry
      const updated = await api.getApiV1PlanningEntriesId(id);
      set((state) => ({
        entries: state.entries.map((entry) =>
          // why: the single-entry DTO carries no planned vehicle — keep the list's (FACF-118).
          entry.planningEntryId === id ? { ...entry, ...updated } : entry
        ),
      }));
    } catch (err) {
      set({ error: 'Fehler beim Aktualisieren des Eintrags' });
      throw err;
    }
  },

  setPlannedVehicle: async (id, vehicleId, driverRole) => {
    // An omitted role reaches the server as null, which clears it together with the vehicle.
    const planned = await api.putApiV1PlanningEntriesIdVehicle(id, { vehicleId, driverRole: driverRole ?? undefined });
    set((state) => ({
      entries: state.entries.map((entry) =>
        entry.planningEntryId === id
          ? {
              ...entry,
              plannedVehicleId: planned.vehicleId ?? null,
              plannedLicensePlate: planned.licensePlate ?? null,
              plannedDriverRole: planned.driverRole,
            }
          : entry
      ),
    }));
  },

  deleteEntry: async (id: string) => {
    try {
      await api.deleteApiV1PlanningEntriesId(id);
      // Remove from local state
      set((state) => ({
        entries: state.entries.filter((e) => e.planningEntryId !== id),
      }));
    } catch (err) {
      set({ error: 'Fehler beim Löschen des Eintrags' });
      throw err;
    }
  },

  addTaskToEntry: async (planningEntryId: string, taskId: string, startTime: string, endTime: string) => {
    try {
      const durationMinutes = calculateDurationMinutes(startTime, endTime);
      
      const request: FacilityFlowCoreModulesPlanningDtosPlanningEntriesAddTaskToPlanningEntryRequest = {
        taskId,
        startTime,
        endTime,
        durationMinutes,
        sequenceOrder: 0, // Auto-reordered by backend
      };

      await api.postApiV1PlanningEntriesIdTasks(planningEntryId, request);
      
      // Refresh only the affected entry
      const updated = await api.getApiV1PlanningEntriesId(planningEntryId);
      set((state) => ({
        entries: state.entries.map((entry) =>
          entry.planningEntryId === planningEntryId ? updated : entry
        ),
      }));
    } catch (err) {
      set({ error: 'Fehler beim Hinzufügen der Aufgabe' });
      throw err;
    }
  },

  assignTaskToEmployees: async (request) => {
    try {
      await api.postApiV1PlanningEntriesTasksAssign(request);

      const employeeIds = new Set(request.employeeIds ?? []);
      const day = request.date?.slice(0, 10);
      const affectedIds = get()
        .entries.filter(
          (entry) =>
            entry.employeeId !== undefined &&
            employeeIds.has(entry.employeeId) &&
            entry.date?.slice(0, 10) === day &&
            entry.entryType === 'WorkDay'
        )
        .map((entry) => entry.planningEntryId)
        .filter((id): id is string => Boolean(id));
      await get().refreshMultipleEntries(affectedIds);
    } catch (err) {
      set({ error: 'Fehler beim Hinzufügen der Aufgabe' });
      throw err;
    }
  },

  removeTaskFromEntry: async (planningEntryId: string, planningEntryTaskId: string) => {
    try {
      await api.deleteApiV1PlanningEntriesIdTasksTaskId(planningEntryId, planningEntryTaskId);
      
      // Refresh only the affected entry
      const updated = await api.getApiV1PlanningEntriesId(planningEntryId);
      set((state) => ({
        entries: state.entries.map((entry) =>
          entry.planningEntryId === planningEntryId ? updated : entry
        ),
      }));
    } catch (err) {
      set({ error: 'Fehler beim Entfernen der Aufgabe' });
      throw err;
    }
  },

  moveTaskToEntry: async (planningEntryTaskId: string, sourcePlanningEntryId: string, targetPlanningEntryId: string, newStartTime?: string, newEndTime?: string) => {
    try {
      await api.postApiV1PlanningEntriesTasksPlanningEntryTaskIdMove(planningEntryTaskId, {
        targetPlanningEntryId,
        newStartTime: newStartTime || null,
        newEndTime: newEndTime || null,
        newSequenceOrder: null, // Auto-append at the end
      });
      
      // Refresh both source and target entries
      const updates = await Promise.all([
        api.getApiV1PlanningEntriesId(sourcePlanningEntryId),
        api.getApiV1PlanningEntriesId(targetPlanningEntryId)
      ]);
      
      set((state) => {
        const updatedEntries = [...state.entries];
        updates.forEach(updated => {
          const index = updatedEntries.findIndex(e => e.planningEntryId === updated.planningEntryId);
          if (index !== -1) {
            updatedEntries[index] = updated;
          }
        });
        return { entries: updatedEntries };
      });
    } catch (err) {
      set({ error: 'Fehler beim Verschieben der Aufgabe' });
      throw err;
    }
  },

  updateTask: async (planningEntryId: string, planningEntryTaskId: string, startTime: string, endTime: string, notes?: string) => {
    try {
      const durationMinutes = calculateDurationMinutes(startTime, endTime);
      
      await api.putApiV1PlanningEntriesIdTasksTaskId(planningEntryId, planningEntryTaskId, {
        startTime,
        endTime,
        durationMinutes,
        sequenceOrder: null, // Keep current order
        notes: notes || null,
      });
      
      // Refresh only the affected entry
      const updated = await api.getApiV1PlanningEntriesId(planningEntryId);
      set((state) => ({
        entries: state.entries.map((entry) =>
          entry.planningEntryId === planningEntryId ? updated : entry
        ),
      }));
    } catch (err) {
      set({ error: 'Fehler beim Aktualisieren der Aufgabe' });
      throw err;
    }
  },

  bulkUpdateTasks: async (request) => {
    try {
      const response = await api.putApiV1PlanningEntriesTasksBulkUpdate(request);
      return response;
    } catch (err) {
      set({ error: 'Fehler beim Aktualisieren der Aufgaben' });
      throw err;
    }
  },

  generateMonthEntries: async (employeeIds: string[], month: number, year: number) => {
    try {
      // Calculate first and last day of month
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);

      // Generate planning entries for all workdays (Mon-Fri) in the month
      const entriesToCreate: FacilityFlowCoreModulesPlanningDtosPlanningEntriesCreatePlanningEntryRequest[] = [];

      for (const employeeId of employeeIds) {
        const currentDate = new Date(firstDay);

        while (currentDate <= lastDay) {
          const dayOfWeek = currentDate.getDay();

          // Only workdays (Mon-Fri = 1-5)
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            const dateStr = isoDate(currentDate);

            entriesToCreate.push({
              employeeId,
              date: dateStr,
              entryType: 'workday',
              startTime: null, // Backend will use model's default
              endTime: null,
              breakMinutes: 0,
              targetHours: null, // Backend calculates from model
              tasks: null,
              notes: null,
              absenceTypeId: null,
            });
          }

          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      // Create all entries (backend will skip existing ones)
      for (const request of entriesToCreate) {
        try {
          await api.postApiV1PlanningEntries(request);
        } catch {
          // Ignore errors for existing entries (409 Conflict)
          // Silently skip to avoid clutter
        }
      }
    } catch (err) {
      set({ error: 'Fehler beim Generieren der Monatseinträge' });
      throw err;
    }
  },
}));

function calculateDurationMinutes(startTime: string, endTime: string): number {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  return (endH * 60 + endM) - (startH * 60 + startM);
}
